import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M12_ROLES, hasAnyRole } from '../../lib/roles';
import type { ApplicantType, ImportExportPermit, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const TABS = [
  { key: 'apply', label: 'Apply' },
  { key: 'mine', label: 'My permits' },
  { key: 'all', label: 'All permits (staff)' },
] as const;

export function Permits() {
  const { persona } = useAuth();
  const isOfficer = hasAnyRole(persona?.roles, M12_ROLES.TRADE_OFFICER);
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('apply');

  return (
    <div>
      <PageHeader title="Import / Export Permits" subtitle="Trade documents with competent-authority referral, derived from each good's HS chapter." />
      <Tabs tabs={isOfficer ? TABS : TABS.filter((t) => t.key !== 'all')} value={tab} onChange={setTab} />
      {tab === 'apply' ? <ApplyForm /> : tab === 'mine' ? <MinePermits /> : <AllPermits />}
    </div>
  );
}

function ApplyForm() {
  const { persona } = useAuth();
  const options: Array<{ type: ApplicantType; id: string; label: string }> = [];
  if (persona?.nin) options.push({ type: 'PERSON', id: persona.nin, label: `Myself (${persona.nin})` });
  if (persona?.actingForBusinessId) options.push({ type: 'ENTITY', id: persona.actingForBusinessId, label: `My business (${persona.actingForBusinessId})` });

  // Hooks called unconditionally (never after an early return) — the guards
  // below only decide what to render, not whether these run.
  const [applicantIdx, setApplicantIdx] = useState(0);
  const [direction, setDirection] = useState<'IMPORT' | 'EXPORT'>('IMPORT');
  const [hsCodes, setHsCodes] = useState('');
  const [goodsDescription, setGoodsDescription] = useState('');
  const [originCountry, setOriginCountry] = useState('SC');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [declaredValue, setDeclaredValue] = useState('');
  const [currency, setCurrency] = useState<'SCR' | 'USD' | 'EUR'>('SCR');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<ImportExportPermit | null>(null);

  if (!persona) return <InfoNote message="Sign in to apply for an import/export permit." />;
  if (options.length === 0) return <InfoNote message="Your account has no NIN or business mandate on file — sign in as a trader/investor persona." />;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const applicant = options[applicantIdx]!;
      const res = await api.post<ImportExportPermit>(
        '/api/v1/m12/import-export-permits',
        {
          direction,
          applicantType: applicant.type,
          applicantId: applicant.id,
          hsCodes: hsCodes.split(',').map((s) => s.trim()).filter(Boolean),
          goodsDescription,
          originCountry: originCountry.toUpperCase(),
          destinationCountry: destinationCountry.toUpperCase(),
          declaredValue: Number.parseFloat(declaredValue),
          currency,
        },
        { actingFor: applicant.type === 'ENTITY' ? applicant.id : undefined, idempotencyKey: newIdempotencyKey() },
      );
      setCreated(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Application failed');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="card border-emerald-200 bg-emerald-50 p-5">
        <h2 className="text-sm font-bold text-emerald-800">Permit application {created.refNo} submitted</h2>
        <p className="mt-1 text-xs text-emerald-700">
          State: {created.state}. {created.referrals.length > 0 ? `Referred to ${created.referrals.map((r) => r.authorityCode).join(', ')} for clearance.` : 'No competent-authority referral required for these HS codes.'}
        </p>
        <button className="btn-secondary mt-3 !bg-white" onClick={() => setCreated(null)}>Apply for another</button>
      </div>
    );
  }

  return (
    <div className="card p-5">
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="space-y-3">
        {options.length > 1 ? (
          <Field label="Applying as">
            <select className="field-input" value={applicantIdx} onChange={(e) => setApplicantIdx(Number(e.target.value))}>
              {options.map((o, i) => <option key={o.id} value={i}>{o.label}</option>)}
            </select>
          </Field>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Direction">
            <select className="field-input" value={direction} onChange={(e) => setDirection(e.target.value as typeof direction)}>
              <option value="IMPORT">Import</option>
              <option value="EXPORT">Export</option>
            </select>
          </Field>
          <Field label="HS codes (comma separated)"><input className="field-input" value={hsCodes} onChange={(e) => setHsCodes(e.target.value)} placeholder="0301.10, 0302.11" /></Field>
        </div>
        <Field label="Goods description"><textarea className="field-input" rows={2} value={goodsDescription} onChange={(e) => setGoodsDescription(e.target.value)} /></Field>
        <div className="grid grid-cols-4 gap-3">
          <Field label="Origin (ISO2)"><input className="field-input" value={originCountry} onChange={(e) => setOriginCountry(e.target.value.toUpperCase())} maxLength={2} /></Field>
          <Field label="Destination (ISO2)"><input className="field-input" value={destinationCountry} onChange={(e) => setDestinationCountry(e.target.value.toUpperCase())} maxLength={2} /></Field>
          <Field label="Declared value"><input className="field-input" type="number" min="0" value={declaredValue} onChange={(e) => setDeclaredValue(e.target.value)} /></Field>
          <Field label="Currency">
            <select className="field-input" value={currency} onChange={(e) => setCurrency(e.target.value as typeof currency)}>
              <option value="SCR">SCR</option><option value="USD">USD</option><option value="EUR">EUR</option>
            </select>
          </Field>
        </div>
        <button
          className="btn-primary"
          onClick={submit}
          disabled={busy || hsCodes.trim().length === 0 || goodsDescription.length < 3 || destinationCountry.length !== 2 || !declaredValue}
        >
          {busy ? 'Submitting…' : 'Submit application'}
        </button>
      </div>
    </div>
  );
}

function MinePermits() {
  const { persona } = useAuth();
  // Sent when available so ENTITY-mode permits (applied under a business
  // mandate) show up too — the /me route works off `nin` alone otherwise.
  const mineQ = useApiQuery<{ items: ImportExportPermit[] }>('/api/v1/m12/import-export-permits/me', persona?.actingForBusinessId);
  return (
    <div>
      {mineQ.error ? <ErrorNote message={mineQ.error} /> : null}
      {mineQ.loading ? <Spinner /> : (mineQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No permits yet" /> : (
        <div className="space-y-3">{mineQ.data!.items.map((p) => <PermitCard key={p.id} permit={p} onChanged={mineQ.reload} />)}</div>
      )}
    </div>
  );
}

function PermitCard({ permit, onChanged }: { permit: ImportExportPermit; onChanged: () => void }) {
  const { persona } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canWithdraw = !['ISSUED', 'REJECTED', 'WITHDRAWN', 'REVOKED'].includes(permit.state);

  const withdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(
        `/api/v1/m12/import-export-permits/${permit.refNo}/withdraw`,
        {},
        { actingFor: permit.applicantType === 'ENTITY' ? persona?.actingForBusinessId : undefined },
      );
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Withdraw failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Link to={`/trade/permits/${permit.refNo}`} className="card block p-4 hover:border-brand-300">
      <div className="flex items-center justify-between">
        <span className="font-mono text-sm font-semibold text-slate-800">{permit.refNo}</span>
        <StatusBadge status={permit.state} />
      </div>
      <div className="mt-1 text-xs text-slate-500">{permit.direction} · {permit.goodsDescription}</div>
      {permit.referrals.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-1">{permit.referrals.map((r) => <span key={r.id} className="text-[11px] text-slate-400">{r.authorityCode}:<StatusBadge status={r.status} /></span>)}</div>
      ) : null}
      {error ? <div className="mt-2"><ErrorNote message={error} /></div> : null}
      {canWithdraw ? (
        <button className="btn-secondary mt-2 !px-2.5 !py-1 text-xs" onClick={(e) => { e.preventDefault(); withdraw(); }} disabled={busy}>{busy ? 'Withdrawing…' : 'Withdraw'}</button>
      ) : null}
    </Link>
  );
}

function AllPermits() {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const query = new URLSearchParams({ limit: '50' });
  if (status) query.set('status', status);
  if (q) query.set('q', q);
  const listQ = useApiQuery<Page<ImportExportPermit>>(`/api/v1/m12/import-export-permits?${query.toString()}`);

  return (
    <div>
      <div className="mb-4 grid gap-2 sm:grid-cols-2">
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['SUBMITTED', 'REFERRAL_PENDING', 'CLEARED', 'FEE_PENDING', 'ISSUED', 'REJECTED', 'WITHDRAWN', 'REVOKED'].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Search" className="!mb-0"><input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ref no, applicant, description…" /></Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? <Spinner /> : (listQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No permits match" /> : (
          <table className="w-full">
            <thead><tr><th className="table-head">Ref</th><th className="table-head">Direction</th><th className="table-head">Applicant</th><th className="table-head">Status</th></tr></thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs"><Link className="text-brand-600 hover:underline" to={`/trade/permits/${p.refNo}`}>{p.refNo}</Link></td>
                  <td className="table-cell text-xs">{p.direction}</td>
                  <td className="table-cell font-mono text-xs text-slate-500">{p.applicantId}</td>
                  <td className="table-cell"><StatusBadge status={p.state} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
